import React from "react";
import { ListDetail } from "../web/ListDetail";
import { EmptyDetailCard } from "../core/EmptyDetailCard";
import { Path } from "../Path";
import { useNavigate } from "react-router-dom";
import { ListView } from "../core/ListView";
import Badge from "@mui/material/Badge";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import StackedBarChart from "@mui/icons-material/StackedBarChart";
import Timeline from "@mui/icons-material/Timeline";
import { useCategoryFromRoute } from "./routes";
import Alert from "@mui/material/Alert";
import { AlwaysOnAppLink } from "../styleguide/AppLink";
import { useGameIdFromRoute } from "../games/WithGameIdFromRoute";
import Box from "@mui/material/Box";
import { ScoreComposition } from "./ScoreComposition";
import { DurationOverTime } from "./DurationOverTime";
import ScatterPlot from "@mui/icons-material/ScatterPlot";
import { PlayerCountVsDuration } from "./PlayerCountVsDuration";
import { AudienceDistribution } from "./AudienceDistribution";
import { CumulativePlayTests } from "./CumulativePlayTests";
import { MediumDistribution } from "./MediumDistribution";
import { PlayerCountDistribution } from "./PlayerCountDistribution";
import People from "@mui/icons-material/People";
import TrendingUp from "@mui/icons-material/TrendingUp";
import Computer from "@mui/icons-material/Computer";
import Group from "@mui/icons-material/Group";

/**
 * @param {Object} props
 * @param {string} props.id
 * @param {string} props.category
 * @param {string} props.helperText
 * @param {boolean} props.selected
 * @param {Function} props.onClick
 * @param {number} props.badgeCount
 * @param {import("@mui/material/OverridableComponent").OverridableComponent<import("@mui/material").SvgIconTypeMap<{}, "svg">>} props.Icon
 */
const SettingsCategory = ({ id, category, helperText, selected, onClick, badgeCount = 0, Icon }) => {
  const onClickHandler = () => {
    onClick(id);
  };

  return (
    <ListItemButton alignItems="flex-start" selected={selected} onClick={onClickHandler}>
      <ListItemAvatar>
        <Badge color="primary" badgeContent={badgeCount}>
          <Icon />
        </Badge>
      </ListItemAvatar>
      <ListItemText primary={category} secondary={helperText} />
    </ListItemButton>
  );
};

export const ChartOptions = ({ onListItemSelected, selectedListItemId }) => {
  const gameId = useGameIdFromRoute();

  return (
    <ListView title="Play Test Charts" Filters={null} Action={undefined} EmptyList={EmptyDetailCard} neverEmpty>
      <Box p={1}>
        <Alert severity="info">
          Additional charts can be found by drilling into{" "}
          <AlwaysOnAppLink to={Path.game(gameId).metadata()}>play test metadata</AlwaysOnAppLink>.
        </Alert>
      </Box>
      <SettingsCategory
        category="Score - Composition"
        id="score-composition"
        helperText="View break up of score by parts"
        selected={selectedListItemId === "score-composition"}
        onClick={onListItemSelected}
        badgeCount={0}
        Icon={StackedBarChart}
      />
      <SettingsCategory
        category="Duration - Over Time"
        id="duration-over-time"
        helperText="View how play test duration changes over time"
        selected={selectedListItemId === "duration-over-time"}
        onClick={onListItemSelected}
        badgeCount={0}
        Icon={Timeline}
      />
      <SettingsCategory
        category="Player Count vs Duration"
        id="player-count-vs-duration"
        helperText="View relationship between player count and game duration"
        selected={selectedListItemId === "player-count-vs-duration"}
        onClick={onListItemSelected}
        badgeCount={0}
        Icon={ScatterPlot}
      />
      <SettingsCategory
        category="Audience Distribution"
        id="play-audience-distribution"
        helperText="View distribution of play tests by audience type"
        selected={selectedListItemId === "play-audience-distribution"}
        onClick={onListItemSelected}
        badgeCount={0}
        Icon={People}
      />
      <SettingsCategory
        category="Cumulative Play Tests"
        id="play-count-distribution"
        helperText="View cumulative play tests over time"
        selected={selectedListItemId === "play-count-distribution"}
        onClick={onListItemSelected}
        badgeCount={0}
        Icon={TrendingUp}
      />
      <SettingsCategory
        category="In-Person vs Digital"
        id="play-method-distribution"
        helperText="View distribution of in-person vs digital play tests"
        selected={selectedListItemId === "play-method-distribution"}
        onClick={onListItemSelected}
        badgeCount={0}
        Icon={Computer}
      />
      <SettingsCategory
        category="Player Count Distribution"
        id="player-count-distribution"
        helperText="View distribution of play tests by number of players"
        selected={selectedListItemId === "player-count-distribution"}
        onClick={onListItemSelected}
        badgeCount={0}
        Icon={Group}
      />
    </ListView>
  );
};

export const PlayTestChartList = () => {
  const gameId = useGameIdFromRoute();
  const category = useCategoryFromRoute();
  const navigate = useNavigate();

  const onListItemSelected = (id) => {
    navigate(Path.game(gameId).chart(id));
  };

  const SelectedCategory = () => {
    if (category === "score-composition") {
      return <ScoreComposition gameId={gameId} />;
    }

    if (category === "duration-over-time") {
      return <DurationOverTime gameId={gameId} />;
    }

    if (category === "player-count-vs-duration") {
      return <PlayerCountVsDuration gameId={gameId} />;
    }

    if (category === "play-audience-distribution") {
      return <AudienceDistribution gameId={gameId} />;
    }

    if (category === "play-count-distribution") {
      return <CumulativePlayTests gameId={gameId} />;
    }

    if (category === "play-method-distribution") {
      return <MediumDistribution gameId={gameId} />;
    }

    if (category === "player-count-distribution") {
      return <PlayerCountDistribution gameId={gameId} />;
    }

    return <EmptyDetailCard text="Select setting group from list on the left" />;
  };

  const ListView = () => <ChartOptions selectedListItemId={category} onListItemSelected={onListItemSelected} />;

  return <ListDetail ListView={ListView} DetailView={SelectedCategory} />;
};
